import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { dlog } from '../utils/debug';
import {
  queryGeoPermission,
  getCurrentLocation,
  computeVenueId,
  computeVenueIdGrid,
} from '../utils/geo';
import { CaretLeft } from 'phosphor-react';
import { DynamicFlapperLoader } from '../components';
import { getContrastTextColor } from '../utils/contrast';
import { getMatchStatus } from '../utils/matchCompletion';
import SetDisplay from '../components/SetDisplay';
import '../styles/LandingPage.css';
import './NearbyMatchesPage.css';

/**
 * Full page (not a modal) for the "Matches near you" discovery
 * surface. Mounted at /nearby. Uses the same .landing shell + match
 * row visual as LandingPage so it reads as a sibling page, not a
 * different product. Drawer entry navigates here.
 *
 * State flow mirrors what the old NearbyMatchesModal did but renders
 * inline (no modal layering), with each state owning the entire
 * match-content area.
 */
function NearbyMatchesPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, ensureAnonymousUser } = useAuth();

  // 'checking' | 'prompt' | 'enabling' | 'granted' | 'denied' | 'unsupported'
  const [permission, setPermission] = useState('checking');
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);

  // Lazy-anon-auth so the user has a uid for filtering own matches.
  // CRITICAL: wait for auth to finish resolving before firing.
  // Otherwise a signed-in user landing here during the brief Firebase
  // Auth rehydration window gets their session overwritten by a
  // fresh anonymous sign-in (transient null on both user + auth.
  // currentUser → signInAnonymously wins the race).
  useEffect(() => {
    if (authLoading) return;
    if (user) return;
    ensureAnonymousUser();
  }, [user, authLoading, ensureAnonymousUser]);

  // Fetch matches in the 3×3 venueId grid centered on the user. The
  // venueId index is deployed to RTDB now, so we can use the indexed
  // query path (faster than the client-side filter we used as a
  // workaround pre-deploy). Falls back to client-filter only if
  // explicitly needed.
  const fetchMatchesAt = useCallback(
    async (preloadedLoc) => {
      setLoadingMatches(true);
      try {
        const loc = preloadedLoc || (await getCurrentLocation());
        const grid = computeVenueIdGrid(loc.lat, loc.lng);
        const gridSet = new Set(grid);

        const matchesRef = ref(db, 'matches');
        const snapshot = await get(matchesRef);

        const list = [];
        if (snapshot.exists()) {
          snapshot.forEach((child) => {
            const m = child.val();
            if (!m || !m.venueId) return;
            if (!gridSet.has(m.venueId)) return;
            // Skip the viewer's own matches — discovery is for others'.
            if (m.creatorId && m.creatorId === user?.uid) return;
            list.push({ id: child.key, ...m });
          });
        }
        // Live first, then most recent.
        list.sort((a, b) => {
          const aLive = a.status !== 'final' ? 1 : 0;
          const bLive = b.status !== 'final' ? 1 : 0;
          if (aLive !== bLive) return bLive - aLive;
          return (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0);
        });
        setMatches(list);
        dlog(`NearbyMatchesPage: ${list.length} matches near ${computeVenueId(loc.lat, loc.lng)}`);
      } catch (err) {
        dlog('NearbyMatchesPage: load failed:', err);
        setMatches([]);
      } finally {
        setLoadingMatches(false);
      }
    },
    [user?.uid]
  );

  // On mount: peek at permission. If already granted, fetch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await queryGeoPermission();
      if (cancelled) return;
      setPermission(p === 'unsupported' ? 'unsupported' : p);
      if (p === 'granted') fetchMatchesAt();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, []);

  const handleEnable = async () => {
    setPermission('enabling');
    try {
      const loc = await getCurrentLocation();
      setPermission('granted');
      fetchMatchesAt(loc);
    } catch (err) {
      setPermission(err.code === 'denied' ? 'denied' : 'prompt');
    }
  };

  const handleOpen = (id) => {
    navigate(`/match/${id}`);
  };

  return (
    <div className="landing">
      <div className="match-header nearby-page-header">
        <button
          type="button"
          className="menu-btn"
          onClick={() => navigate('/')}
          aria-label="Back"
        >
          <CaretLeft size={24} weight="bold" />
        </button>
        <h1 className="nearby-page-title">Matches near you</h1>
      </div>

      <div className="match-content">
        {permission === 'checking' && (
          <div className="nearby-page-loader">
            <DynamicFlapperLoader size="medium" />
          </div>
        )}

        {permission === 'prompt' && (
          <div className="nearby-empty-state">
            <p className="eyebrow">Find matches near you</p>
            <h2 className="nearby-empty-headline">See what's happening here</h2>
            <p className="nearby-empty-body">
              We use your device's location once, only to find matches
              at this venue. Never stored, never tracked.
            </p>
            <button
              type="button"
              className="nearby-page-primary nearby-empty-cta"
              onClick={handleEnable}
              disabled={permission === 'enabling'}
            >
              Enable location
            </button>
          </div>
        )}

        {permission === 'enabling' && (
          <div className="nearby-page-loader">
            <DynamicFlapperLoader size="medium" />
          </div>
        )}

        {permission === 'denied' && (
          <div className="nearby-empty-state">
            <p className="eyebrow">Location is off</p>
            <h2 className="nearby-empty-headline">Turn it on to discover</h2>
            <p className="nearby-empty-body">
              Enable location in your browser settings to find matches
              happening at your venue.
            </p>
          </div>
        )}

        {permission === 'unsupported' && (
          <div className="nearby-empty-state">
            <p className="nearby-empty-body">
              Your browser doesn't support location features. Try Safari
              or Chrome on a mobile device.
            </p>
          </div>
        )}

        {permission === 'granted' && loadingMatches && (
          <div className="nearby-page-loader">
            <DynamicFlapperLoader size="medium" />
          </div>
        )}

        {permission === 'granted' && !loadingMatches && matches.length === 0 && (
          <div className="nearby-empty-state">
            <p className="eyebrow">You're the first here</p>
            <h2 className="nearby-empty-headline">Set the scene</h2>
            <p className="nearby-empty-body">
              Score a match at this venue and other folks nearby will
              find it here.
            </p>
            <button
              type="button"
              className="nearby-page-primary nearby-empty-cta"
              onClick={() => navigate('/')}
            >
              Start a match
            </button>
          </div>
        )}

        {permission === 'granted' && !loadingMatches && matches.length > 0 && (
          <div className="match-list">
            {matches.map((match) => {
              const { id, teamA = {}, teamB = {}, sets = [], score = {}, setsWon = {}, status: storedStatus } = match;
              const teamASetWins = setsWon?.teamA || 0;
              const teamBSetWins = setsWon?.teamB || 0;
              const dynamicStatus = getMatchStatus(setsWon, sets, true);
              const status = storedStatus || dynamicStatus;

              let leader = null;
              if (teamASetWins > teamBSetWins) leader = 'teamA';
              else if (teamBSetWins > teamASetWins) leader = 'teamB';
              else if ((score?.teamA || 0) > (score?.teamB || 0)) leader = 'teamA';
              else if ((score?.teamB || 0) > (score?.teamA || 0)) leader = 'teamB';

              let bgColor = '#999';
              if (leader === 'teamA') bgColor = teamA?.color || '#ffcc00';
              else if (leader === 'teamB') bgColor = teamB?.color || '#0066ff';

              const textColor = getContrastTextColor(bgColor);
              const isFinal = status === 'final';

              return (
                <div
                  key={id}
                  className={`row ${textColor === '#ffffff' ? 'dark-bg' : 'light-bg'}`}
                  style={{ backgroundColor: bgColor, color: textColor, cursor: 'pointer' }}
                  onClick={() => handleOpen(id)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleOpen(id);
                  }}
                >
                  <div className="team-names">
                    <div className="team-name" style={{ opacity: leader === 'teamB' ? 0.65 : 1 }}>
                      <span className="team-name-text">{teamA?.name || 'Team A'}</span>
                    </div>
                    <div className="team-name" style={{ opacity: leader === 'teamA' ? 0.65 : 1 }}>
                      <span className="team-name-text">{teamB?.name || 'Team B'}</span>
                    </div>
                  </div>

                  <SetDisplay sets={sets} leader={leader} />

                  <div className="match-status-wrapper">
                    <div
                      className={`match-status match-status--${isFinal ? 'final' : 'playing'}`}
                    >
                      {isFinal ? 'FINAL' : 'LIVE'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default NearbyMatchesPage;
